/**
 * PackageTitle
 * Typographical title showing channel, package type, and recipient.
 * Format: [Channel] [Type] to [Recipient]
 */

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { CorrespondenceChannel } from '@/services/api/correspondenceService';

import type { CorrespondencePackageType } from '../types';
import { CHANNEL_CONFIG, PACKAGE_TYPE_DISPLAY } from './constants';

interface PackageTitleProps {
  packageType: CorrespondencePackageType;
  channel: CorrespondenceChannel | null;
  recipientName: string | null | undefined;
  /** Related carrier name from coverage (shown when no recipient yet) */
  relatedCarrierName?: string | null;
  date?: string | null;
  className?: string;
}

/** Simple channel label (icon + text) for inline use */
const ChannelLabel: React.FC<{
  channel: CorrespondenceChannel;
  className?: string;
}> = ({ channel, className }) => {
  const config = CHANNEL_CONFIG[channel];
  return (
    <span
      className={cn('inline-flex items-center gap-1', className)}
      title={config.description}
    >
      <span className="text-[11px]">{config.icon}</span>
      {config.shortLabel}
    </span>
  );
};

/** Channel badge with icon and tooltip (for headers) */
const ChannelBadge: React.FC<{
  channel: CorrespondenceChannel;
  className?: string;
}> = ({ channel, className }) => {
  const config = CHANNEL_CONFIG[channel];
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            'inline-flex items-center gap-1 px-2 py-0.5 rounded-md',
            'bg-zinc-100 text-zinc-600 text-xs font-medium',
            'hover:bg-zinc-200 transition-colors cursor-default',
            className,
          )}
        >
          <span className="text-[10px]">{config.icon}</span>
          {config.shortLabel}
        </span>
      </TooltipTrigger>
      <TooltipContent
        side="bottom"
        sideOffset={8}
        className="text-xs px-3 py-2"
      >
        {config.description}
      </TooltipContent>
    </Tooltip>
  );
};

/** Package type badge */
const TypeBadge: React.FC<{
  packageType: CorrespondencePackageType;
  className?: string;
}> = ({ packageType, className }) => {
  const config = PACKAGE_TYPE_DISPLAY[packageType];
  // Don't show badge for custom packages
  if (packageType === 'custom') return null;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            'inline-flex items-center px-2 py-0.5 rounded-md',
            'bg-indigo-50 text-indigo-700 text-xs font-medium',
            'hover:bg-indigo-100 transition-colors cursor-default',
            className,
          )}
        >
          {config.shortLabel}
        </span>
      </TooltipTrigger>
      <TooltipContent
        side="bottom"
        sideOffset={8}
        className="text-xs max-w-[220px] px-3 py-2"
      >
        Started with standard {config.label.toLowerCase()} documents. You can
        add, remove, or edit any of them.
      </TooltipContent>
    </Tooltip>
  );
};

/** Recipient display */
const RecipientName: React.FC<{
  name: string;
  className?: string;
}> = ({ name, className }) => (
  <span className={cn('text-sm font-semibold text-zinc-900', className)}>
    {name}
  </span>
);

export const PackageTitle: React.FC<PackageTitleProps> = ({
  packageType,
  channel,
  recipientName,
  relatedCarrierName,
  date,
  className,
}) => {
  const hasRecipient = recipientName && recipientName.trim().length > 0;
  const hasRelatedCarrier =
    !hasRecipient && relatedCarrierName && relatedCarrierName.trim().length > 0;

  return (
    <div className={cn('flex items-center gap-2 flex-wrap', className)}>
      {/* Optional date prefix */}
      {date && (
        <>
          <span className="text-xs text-zinc-400 font-medium tabular-nums">
            {new Date(date).toLocaleDateString('en-US', {
              month: 'numeric',
              day: 'numeric',
              year: 'numeric',
            })}
          </span>
          <span className="text-zinc-300">·</span>
        </>
      )}

      {/* Channel badge */}
      {channel && <ChannelBadge channel={channel} />}

      {/* Package type badge (hidden for custom) */}
      <TypeBadge packageType={packageType} />

      {/* Recipient with "to" prefix */}
      {hasRecipient && recipientName && (
        <>
          <span className="text-xs text-zinc-400">to</span>
          <RecipientName name={recipientName} />
        </>
      )}

      {/* Related carrier hint (when no recipient selected yet) */}
      {hasRelatedCarrier && (
        <>
          <span className="text-xs text-zinc-400">for</span>
          <span className="text-sm text-zinc-500 italic">
            {relatedCarrierName}
          </span>
        </>
      )}

      {/* Fallback when no recipient */}
      {!hasRecipient &&
        !hasRelatedCarrier &&
        !channel &&
        packageType === 'custom' && (
          <span className="text-sm font-medium text-zinc-600">New Draft</span>
        )}
    </div>
  );
};

export { ChannelBadge, ChannelLabel, RecipientName, TypeBadge };
